import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Instagram, ExternalLink } from "lucide-react";

const studios = [
  {
    id: "grevenbroich",
    badge: "Stammhaus · seit 2023",
    badgeNew: false,
    name: "Grevenbroich",
    image: "https://images.unsplash.com/photo-1624918959325-4ab1f51306d1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    street: "Kölner Straße 39 (Fußgängerzone!)",
    city: "41515 Grevenbroich",
    phone: "0178 4685450",
    hours: "Mo–Fr 11:00–19:00 · Sa 11:00–17:00",
    instagram: "@royal.tattoo.grevenbroich",
    instagramUrl: "https://instagram.com/royal.tattoo.grevenbroich",
    mapsUrl: "https://maps.google.com/?q=Kölner+Straße+39,+41515+Grevenbroich",
  },
  {
    id: "leverkusen",
    badge: "NEU · Eröffnung Mai 2026",
    badgeNew: true,
    name: "Leverkusen",
    image: "https://images.unsplash.com/photo-1654338776478-b8f90696fd15?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    street: "Kölner Straße 28",
    city: "51379 Leverkusen (Opladen)",
    phone: "0176 66124487",
    hours: "Öffnungszeiten werden noch bekannt gegeben",
    instagram: "@royal.tattoo.leverkusen",
    instagramUrl: "https://instagram.com/royal.tattoo.leverkusen",
    mapsUrl: "https://maps.google.com/?q=Kölner+Straße+28,+51379+Leverkusen",
  },
];

export default function StudiosSection() {
  return (
    <section
      id="studios"
      data-testid="studios-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="section-label">Unsere Standorte</span>
          <h2 className="section-heading">Zwei Studios.<br />Eine Philosophie.</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {studios.map((studio, i) => (
            <motion.div
              key={studio.id}
              data-testid={`studio-${studio.id}`}
              className="group relative overflow-hidden bg-[#141414] border border-[#5A4A1A]/40 hover:border-[#C9A84C]/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(201,168,76,0.1)]"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.7 }}
            >
              {/* Studio Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={studio.image}
                  alt={`Studio ${studio.name}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/40 to-transparent" />
                {/* Badge */}
                <div className={`absolute top-4 left-4 px-3 py-1.5 text-xs font-outfit tracking-[0.1em] uppercase font-medium ${
                  studio.badgeNew
                    ? "bg-[#C9A84C] text-[#0A0A0A] animate-gold-pulse"
                    : "bg-[#0A0A0A]/80 text-[#C9A84C] border border-[#C9A84C]/40"
                }`}>
                  {studio.badge}
                </div>
              </div>

              {/* Info */}
              <div className="p-8">
                <h3 className="font-cormorant text-3xl italic text-[#F0F0F0] mb-6">
                  {studio.name}
                </h3>

                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-3">
                    <MapPin size={15} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#F0F0F0] text-sm">{studio.street}</p>
                      <p className="text-[#888888] text-sm">{studio.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={15} className="text-[#C9A84C] flex-shrink-0" />
                    <a href={`tel:${studio.phone.replace(/\s/g, "")}`} className="text-[#F0F0F0] text-sm hover:text-[#C9A84C] transition-colors">
                      {studio.phone}
                    </a>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={15} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                    <p className="text-[#888888] text-sm">{studio.hours}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Instagram size={15} className="text-[#C9A84C] flex-shrink-0" />
                    <a
                      href={studio.instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#888888] text-sm hover:text-[#C9A84C] transition-colors"
                    >
                      {studio.instagram}
                    </a>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={studio.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`studio-${studio.id}-maps`}
                    className="ghost-btn text-xs py-2.5 px-5 flex items-center gap-2"
                  >
                    <ExternalLink size={13} />
                    Route planen
                  </a>
                  <a
                    href="#kontakt"
                    data-testid={`studio-${studio.id}-contact`}
                    className="gold-btn text-xs py-2.5 px-5"
                  >
                    Termin anfragen
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
